import { useState } from 'react';
import { useJobTaskApi } from '../../../../../clients/quipli-service/job-tasks/job-task-api';
import { useOrderApiV1 } from '../../../../../clients/quipli-service/order/order-api-v1';
import { useAPI } from '../../../../../clients/quipli-service/quipli-service-client';
import { useOrderMedia } from '../../../../order/hooks/use-order-media';
import { InspectionModalNav, isTaskComplete, MediaSection, TaskProps } from '../../inspection-modal/inspection-modal';
import { camelCase, capitalize } from 'lodash';
import { toast } from '../../../../../util/toasts';
import { OrderMediaType } from '../../../../../clients/quipli-service/order/order';
import { Typography } from '../../../../../components/typography/typography';
import { InspectionUnitHeader } from '../inspection-header';
import { Spinner } from '../../../../../components/spinner/spinner';
import '../../inspection-modal/inspection-modal.scss';

/**
 * Media task.
 * @param task - The task to inspect.
 * @param jobId - The job id.
 * @param product - The product to inspect.
 * @param unit - The unit to inspect.
 * @param unitTrackingVia - The unit tracking via to use.
 * @param next - The next function.
 * @param back - The back function.
 * @param isPreview - Whether the task is in preview mode (in the edit view)
 * @param setJob - The function to set the job.
 * @param order - The order to inspect.
 * @param isFirstTask - Whether this is the first task in the inspection.
 * @param isLastTask - Whether this is the last task in the inspection.
 */
export const MediaTask = ({
  task,
  jobId,
  product,
  unit,
  unitTrackingVia,
  next,
  back,
  order,
  isPreview = false,
  setJob,
  isFirstTask = false,
  isLastTask = false,
}: TaskProps) => {
  const { updateTask } = useJobTaskApi();
  const { getUploadOrderMediaUrl, addOrderMedia } = useOrderApiV1();
  const { uploadToS3 } = useAPI();
  const { isOrderMediaLoading, mediaList, fetch } = useOrderMedia(order);

  const [isUploading, setIsUploading] = useState(false);
  const [media, setMedia] = useState(
    task.metadata?.media ??
      task.metadata?.mediaSections.reduce(
        (acc: Record<string, string[]>, section: string) => ({ ...acc, [camelCase(section)]: [] }),
        {} as Record<string, string[]>,
      ),
  );

  const saveTask = async (forward: boolean) => {
    if (isPreview || !task || !task.metadata) return;
    try {
      const updatedJob = await updateTask(jobId, task.id, isTaskComplete(task, product), {
        ...task.metadata,
        media: { ...task.metadata.media, ...media },
      });
      setJob(updatedJob);
      if (forward) next();
      else back();
    } catch (e) {
      console.error(e);
      toast('Error updating task', 'error');
    }
  };

  const upload = async (section: string, files: File[]) => {
    if (order === undefined || isPreview || files.length === 0 || isUploading || isOrderMediaLoading) return;

    setIsUploading(true);

    const uploadedMedia = await Promise.all(
      files.map(async (file) => {
        const type = file.type.match('image/*') ? OrderMediaType.Image : OrderMediaType.Video;

        try {
          const { url: preSignedUrl, mediaId } = await getUploadOrderMediaUrl(
            order.id,
            `Unit ${unit[unitTrackingVia]} - ${capitalize(section)} | ${file.name}`,
            type,
          );
          // Save to S3
          await uploadToS3(preSignedUrl, file);
          // Save to DB
          await addOrderMedia(order.id, mediaId, file.name, type);
          toast(`${file.name} uploaded successfully.`);
          return mediaId;
        } catch (error: any) {
          console.error(error);
          toast(
            `Error uploading ${file.name}: ${
              error.message.msg || error.message || error.msg || 'Something went wrong.'
            }`,
            'error',
          );
        } finally {
          await fetch();
          setIsUploading(false);
        }
      }),
    );
    const updatedMedia = { ...media, [section]: [...(media[section] ?? []), ...uploadedMedia] };
    setMedia(updatedMedia);
    const updatedJob = await updateTask(jobId, task.id, 'InProgress', {
      ...task.metadata,
      media: { ...(task.metadata?.media ?? {}), ...updatedMedia },
    });
    setJob(updatedJob);
    await fetch();
  };

  return (
    <div className="media-upload-task-wrapper">
      <Typography className="text-center" type="text-lg">
        {task.header}
      </Typography>
      <InspectionUnitHeader product={product} unitNumber={unit[unitTrackingVia]} />
      <Typography type="text-sm" className="text-grey-500 mb-2">
        {task.instructions}
      </Typography>
      <div className="media-sections-wrapper mt-2">
        {mediaList !== undefined ? (
          task.metadata?.mediaSections.map((section: string) => (
            <MediaSection
              key={section}
              list={mediaList.filter((m) => {
                return (media[camelCase(section)] ?? []).indexOf(m.id) >= 0;
              })}
              isListLoading={false}
              refresh={fetch}
              upload={async (files) => await upload(camelCase(section), files)}
              section={section}
            />
          ))
        ) : (
          <Spinner />
        )}
      </div>
      {!isPreview && <InspectionModalNav saveTask={saveTask} isFirstStep={isFirstTask} isLastStep={isLastTask} />}
    </div>
  );
};
